import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { SmsDocumentUploaderComponent } from '../document-uploader/sms-document-uploader.component';
import { MatButton } from '@angular/material/button';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { SmsAddressFieldsComponent } from '../address-fields/sms-address-fields.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Attribute, CreateRegulationInfoRequest, RegulationApiService, SupportingDocumentInfo } from '@vendasta/smsv2';
import { SmsContextToken } from '../../../../token';
import { SmsContext } from '../../../../interface';
import { map, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { RegulationService } from '../../regulation.service';
import {
  DOCUMENT_ID_CONTROL,
  DOCUMENT_TYPE_CONTROL,
  PROOF_DOCUMENT_CONTROL,
} from '../document-uploader/sms-document-uploader.component';
import { TranslateModule } from '@ngx-translate/core';
import { SMSI18NModule } from '../../../../assets/i18n/sms-i18n.module';
import {
  BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
  CITY_FOR_PROCESSING_ADDRESS,
  COUNTRY_FOR_PROCESSING_ADDRESS,
  POSTAL_CODE_FOR_PROCESSING_ADDRESS,
  STATE_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
} from '../sms-regulatory-form.component';

const AUSTRALIA_COUNTRY_CODE = 'AU';

@Component({
  selector: 'sms-australia-form',
  imports: [
    MatCardModule,
    GalaxyFormFieldModule,
    MatInput,
    SmsDocumentUploaderComponent,
    MatButton,
    GalaxyAlertModule,
    SmsAddressFieldsComponent,
    ReactiveFormsModule,
    TranslateModule,
    SMSI18NModule,
  ],
  templateUrl: './sms-australia-form.component.html',
  styleUrl: './sms-australia-form.component.scss',
})
export class SmsAustraliaFormComponent {
  private readonly snackbarService = inject(SnackbarService);
  private readonly regulationApiService = inject(RegulationApiService);
  private readonly regulationService = inject(RegulationService);
  private readonly context = inject<SmsContext>(SmsContextToken);

  businessIdentityFormGroup = new FormGroup({
    businessName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    businessIdNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  businessAddressFormGroup = new FormGroup({});

  formGroup = new FormGroup({
    businessIdentity: this.businessIdentityFormGroup,
    businessAddress: this.businessAddressFormGroup,
  });

  protected readonly $regulationInfo = toSignal(this.regulationService.regulationInfo$, {
    initialValue: null as RegulationInfo | null,
  });

  protected readonly $regulationStatus = toSignal(this.regulationService.regulationStatus$, {
    initialValue: RegulationStatus.REGULATION_STATUS_UNSPECIFIED,
  });

  protected readonly $shouldFormBeLocked = computed(() => {
    const status = this.$regulationStatus();
    return (
      status === RegulationStatus.REGULATION_STATUS_PENDING_REVIEW ||
      status === RegulationStatus.REGULATION_STATUS_APPROVED
    );
  });

  protected readonly $isRejected = computed(
    () => this.$regulationStatus() === RegulationStatus.REGULATION_STATUS_REJECTED,
  );

  protected readonly $submitting = signal(false);

  constructor() {
    effect(() => {
      this.applySubmissionToForm(this.$regulationInfo());
    });
  }

  private applySubmissionToForm(info: RegulationInfo | null): void {
    if (!info) {
      this.formGroup.enable({ emitEvent: false });
      return;
    }

    const attrs = new Map(info.businessAttributes.map((a) => [a.key, a.value]));
    const identityDoc = info.supportingDocuments.find((d) => !d.processAddress);
    const identityDocAttrs = new Map((identityDoc?.attributes ?? []).map((a) => [a.key, a.value]));
    this.businessIdentityFormGroup.patchValue(
      {
        businessName: attrs.get('business_name') ?? identityDocAttrs.get('business_name') ?? '',
        businessIdNumber: identityDocAttrs.get('document_number') ?? '',
      },
      { emitEvent: false },
    );

    const addressDoc = info.supportingDocuments.find((d) => d.processAddress);
    const addressAttrs = new Map((addressDoc?.attributes ?? []).map((a) => [a.key, a.value]));
    const addressGroup = this.businessAddressFormGroup.get('address') as unknown as FormGroup | null;
    addressGroup?.patchValue(
      {
        streetAddress1: addressAttrs.get(STREET_ADDRESS_FOR_PROCESSING_ADDRESS) ?? '',
        streetAddress2: addressAttrs.get(STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS) ?? '',
        city: addressAttrs.get(CITY_FOR_PROCESSING_ADDRESS) ?? '',
        state: addressAttrs.get(STATE_FOR_PROCESSING_ADDRESS) ?? '',
        postalCode: addressAttrs.get(POSTAL_CODE_FOR_PROCESSING_ADDRESS) ?? '',
      },
      { emitEvent: false },
    );

    if (this.$shouldFormBeLocked()) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
    }
  }

  submitRegulatoryForm() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snackbarService.openErrorSnack('Please fill out all required fields');
      return;
    }
    if (this.$submitting()) return;

    this.$submitting.set(true);
    this.context.ownerId$
      .pipe(
        take(1),
        map((ownerId) => this.convertFormToRequest(ownerId)),
        switchMap((req) => this.regulationApiService.createRegulationInfo(req)),
      )
      .subscribe({
        next: () => {
          this.regulationService.refresh();
          this.$submitting.set(false);
          this.snackbarService.openSuccessSnack('Regulatory information submitted');
        },
        error: () => {
          this.snackbarService.openErrorSnack('Failed to submit regulatory information');
          this.$submitting.set(false);
        },
      });
  }

  private convertFormToRequest(ownerID: string): CreateRegulationInfoRequest {
    let req = new CreateRegulationInfoRequest();
    req.countryCode = AUSTRALIA_COUNTRY_CODE;
    req.ownerId = ownerID;
    req.ownerType = this.context.ownerType;
    req.supportingDocuments = [];

    req = this.addAttributesToRequest(req);
    req = this.addBusinessIdentitySupportingDocument(req);
    req = this.addBusinessAddressSupportingDocument(req);

    return req;
  }

  private addAttributesToRequest(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const businessNameAttribute = new Attribute({
      key: 'business_name',
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    req.businessAttributes = [businessNameAttribute];
    return req;
  }

  private addBusinessIdentitySupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType =
      this.businessIdentityFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.businessIdentityFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    // business_name_info and business_id_no_info both require commercial_registrar_excerpt.
    // Twilio rejects duplicate document types, so these are combined into one supporting document.
    const businessNameAttribute = new Attribute({
      key: 'business_name',
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    const businessIdNumberAttribute = new Attribute({
      key: 'document_number',
      value: this.businessIdentityFormGroup.controls.businessIdNumber.value || '',
    });

    supportingDocumentInfo.attributes = [businessNameAttribute, businessIdNumberAttribute];
    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }

  private addBusinessAddressSupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType =
      this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    supportingDocumentInfo.processAddress = true;
    const businessNameForAddressAttribute = new Attribute({
      key: BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
      value: this.businessIdentityFormGroup.controls.businessName.value || '',
    });
    const streetAddressLine1Attribute = new Attribute({
      key: STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('streetAddress1')?.value || '',
    });
    const cityAttribute = new Attribute({
      key: CITY_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('city')?.value || '',
    });
    const stateAttribute = new Attribute({
      key: STATE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('state')?.value || '',
    });
    const postalCodeAttribute = new Attribute({
      key: POSTAL_CODE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('postalCode')?.value || '',
    });
    const countryAttribute = new Attribute({
      key: COUNTRY_FOR_PROCESSING_ADDRESS,
      value: AUSTRALIA_COUNTRY_CODE,
    });
    supportingDocumentInfo.attributes = [
      businessNameForAddressAttribute,
      streetAddressLine1Attribute,
      cityAttribute,
      stateAttribute,
      postalCodeAttribute,
      countryAttribute,
    ];

    const streetAddressLine2Value = this.businessAddressFormGroup.get('address')?.get('streetAddress2')?.value || '';
    if (streetAddressLine2Value) {
      const streetAddressLine2Attribute = new Attribute({
        key: STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
        value: streetAddressLine2Value,
      });
      supportingDocumentInfo.attributes.push(streetAddressLine2Attribute);
    }

    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }
}
